import { Component, inject, signal, computed, effect, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AsideService } from '../../../core/services/aside.service';
import { InvoiceService } from '../../../core/services/invoice.service';
import { LoadService } from '../../../core/services/load.service';
import { AuthService } from '../../../core/services/auth.service';
import { ToastService } from '../../../shared/ui/toast/toast.service';
import { Invoice, InvoiceLoadSnapshot, InvoiceDocument, InvoiceDocumentType } from '../../../core/models/invoice.model';
import { BadgeComponent } from '../../../shared/ui/badge/badge.component';
import { ButtonComponent } from '../../../shared/ui/button/button.component';
import { ButtonGroupComponent } from '../../../shared/ui/forms/button-group/button-group.component';
import { TextComponent } from '../../../shared/ui/typography/text/text.component';
import { HeadingComponent } from '../../../shared/ui/typography/heading/heading.component';
import { LucideIconComponent } from '../../../shared/ui/lucide-icon/lucide-icon.component';
import { LoadingComponent } from '../../../shared/ui/loading/loading.component';
import { NumberInputComponent } from '../../../shared/ui/forms/number-input/number-input.component';
import { SelectComponent } from '../../../shared/ui/forms/select/select.component';
import { InputComponent } from '../../../shared/ui/forms/input/input.component';
import { ModalService } from '../../../core/services/modal.service';
import { VoidInvoiceModalComponent } from '../void-invoice-modal/void-invoice-modal.component';
import { UploadInvoiceDocumentModalComponent } from '../upload-invoice-document-modal/upload-invoice-document-modal.component';
import { UploadScheduleModalComponent } from '../upload-schedule-modal/upload-schedule-modal.component';
import { RecordPaymentModalComponent } from '../record-payment-modal/record-payment-modal.component';
import { ColorVariant } from '../../../core/enums/types';
import { Subscription, firstValueFrom } from 'rxjs';
import { LoadWithView } from '../../../core/models/load.model';
import { computeLoadLineItem } from '../../../shared/utilities/invoice-pricing';
import { resolveBookedWithCounterparty } from '../../../shared/utilities/counterparty.utils';
import { ResendInvoiceModalComponent } from '../resend-invoice-modal/resend-invoice-modal.component';
import { EditPricingModalComponent } from '../edit-pricing-modal/edit-pricing-modal.component';
import { TonuDecisionModalComponent } from '../tonu-decision-modal/tonu-decision-modal.component';
import { RemoveLoadModalComponent } from '../remove-load-modal/remove-load-modal.component';
import { DocumentViewerModalComponent } from '../../../shared/ui/document-viewer-modal/document-viewer-modal.component';

export interface LiveInvoiceLoad extends InvoiceLoadSnapshot {
  paymentStatus: 'unpaid' | 'paid' | 'partially_paid' | 'denied' | 'problematic';
  paidAmount: number;
}

@Component({
  selector: 'app-invoice-aside',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    BadgeComponent,
    ButtonComponent,
    ButtonGroupComponent,
    TextComponent,
    HeadingComponent,
    LucideIconComponent,
    LoadingComponent,
    NumberInputComponent,
    SelectComponent,
    InputComponent,
  ],
  templateUrl: './invoice-aside.component.html',
  host: { class: 'flex flex-col h-full overflow-x-hidden' },
})
export class InvoiceAsideComponent implements OnDestroy {
  private readonly asideService = inject(AsideService);
  private readonly invoiceService = inject(InvoiceService);
  private readonly loadService = inject(LoadService);
  private readonly authService = inject(AuthService);
  private readonly toastService = inject(ToastService);
  private readonly modalService = inject(ModalService);

  readonly canEditBilling = this.authService.canEditBilling;

  // --- Subscription management ---
  private loadsSub?: Subscription;
  private invoiceSub?: Subscription;

  // --- Active Sub-View ---
  activeSection = signal<'loads' | 'documents'>('loads');

  // --- Core Data ---
  private readonly initialInvoice = computed(() => {
    const ctx = this.asideService.asideContext();
    if (ctx?.type !== 'view-invoice') return null;
    return ctx.data.invoice as Invoice;
  });

  liveInvoice = signal<Invoice | null>(null);

  readonly invoice = computed(() => {
    return this.liveInvoice() || this.initialInvoice();
  });

  readonly documents = computed(() => {
    return this.invoice()?.documents || [];
  });

  readonly factoringScheduleDocs = computed(() => {
    return this.documents().filter(d => d.type === 'factoring_schedule');
  });

  readonly otherDocs = computed(() => {
    return this.documents().filter(d => d.type !== 'factoring_schedule');
  });

  loadSnapshots = signal<LiveInvoiceLoad[]>([]);
  isLoadingLoads = signal<boolean>(false);

  // --- Search ---
  loadsSearchTerm = signal('');

  // --- Per-load payment status change tracking ---
  pendingChanges = signal<Map<string, { status: string; amount: number }>>(new Map());
  savingLoadIds = signal<Set<string>>(new Set());

  // --- Dropdown options ---
  loadPaymentStatusOptions = [
    { value: 'unpaid', label: 'Unpaid' },
    { value: 'paid', label: 'Paid' },
    { value: 'partially_paid', label: 'Partially Paid' },
    { value: 'denied', label: 'Denied' },
    { value: 'problematic', label: 'Problematic' },
  ];

  // --- Computed ---
  filteredLoads = computed(() => {
    const term = this.loadsSearchTerm().toLowerCase();
    const loads = this.loadSnapshots();
    if (!term) return loads;
    return loads.filter(l =>
      (l.externalId && l.externalId.toLowerCase().includes(term)) ||
      (l.loadId && l.loadId.toLowerCase().includes(term)) ||
      (l.counterpartyName && l.counterpartyName.toLowerCase().includes(term))
    );
  });

  balanceDue = computed(() => {
    const inv = this.invoice();
    if (!inv) return 0;
    return Number(((inv.totalAmount || 0) - (inv.feeAmount || 0) - (inv.receivedAmount || 0)).toFixed(2));
  });

  // --- Inline Invoice Financial Summary Editing ---
  isEditingFinancials = signal(false);
  isSavingFinancials = signal(false);
  editBaseFee = signal(0);
  editReserveAmount = signal(0);
  editTransactionFee = signal(0);
  editDirectFeeAmount = signal(0);
  editReceivedAmount = signal(0);

  readonly editTotalFees = computed(() => {
    const inv = this.invoice();
    if (inv?.invoicingMethod === 'factoring') {
      return Number(
        (
          (this.editBaseFee() || 0) +
          (this.editReserveAmount() || 0) +
          (this.editTransactionFee() || 0)
        ).toFixed(2)
      );
    }
    return Number((this.editDirectFeeAmount() || 0).toFixed(2));
  });

  readonly editNetAmount = computed(() => {
    const gross = Number(this.invoice()?.totalAmount || 0);
    return Number((gross - this.editTotalFees()).toFixed(2));
  });

  readonly editBalanceDue = computed(() => {
    return Number(
      (this.editNetAmount() - (this.editReceivedAmount() || 0)).toFixed(2)
    );
  });

  startEditingFinancials(): void {
    const inv = this.invoice();
    if (!inv) return;
    if (inv.invoicingMethod === 'factoring') {
      const ff = inv.factoringFees;
      const base = Number(ff?.baseFee ?? 0);
      const reserve = Number(ff?.reserveAmount ?? 0);
      const tx = Number(ff?.transactionFee ?? inv.scheduleExpensesDeducted ?? 0);
      this.editBaseFee.set(base);
      this.editReserveAmount.set(reserve);
      this.editTransactionFee.set(tx);
    } else {
      this.editDirectFeeAmount.set(Number(inv.feeAmount || 0));
    }
    this.editReceivedAmount.set(Number(inv.receivedAmount || 0));
    this.isEditingFinancials.set(true);
  }

  cancelEditingFinancials(): void {
    this.isEditingFinancials.set(false);
  }

  matchReceivedToNet(): void {
    this.editReceivedAmount.set(Math.max(0, this.editNetAmount()));
  }

  async saveInvoiceFinancials(): Promise<void> {
    const inv = this.invoice();
    if (!inv?.id) return;
    this.isSavingFinancials.set(true);
    try {
      const gross = Number(inv.totalAmount || 0);
      const totalFees = this.editTotalFees();
      const received = Number((this.editReceivedAmount() || 0).toFixed(2));

      let factoringFees = inv.factoringFees;
      let scheduleExpensesDeducted = inv.scheduleExpensesDeducted;

      if (inv.invoicingMethod === 'factoring') {
        const baseFee = Number((this.editBaseFee() || 0).toFixed(2));
        const reserveAmount = Number((this.editReserveAmount() || 0).toFixed(2));
        const transactionFee = Number((this.editTransactionFee() || 0).toFixed(2));
        factoringFees = {
          baseFee,
          reserveAmount,
          transactionFee,
          basePercentage:
            gross > 0 ? Number(((baseFee / gross) * 100).toFixed(2)) : 0,
          reservePercentage:
            gross > 0 ? Number(((reserveAmount / gross) * 100).toFixed(2)) : 0,
        };
        scheduleExpensesDeducted = transactionFee;
      }

      await this.invoiceService.updateInvoiceFinancialSummary(inv.id, {
        receivedAmount: received,
        feeAmount: totalFees,
        factoringFees,
        scheduleExpensesDeducted,
      });

      this.isEditingFinancials.set(false);
      this.toastService.show(
        'success',
        'Invoice amounts and fees updated.'
      );
    } catch (err: any) {
      this.toastService.show(
        'error',
        err?.message || 'Failed to update invoice amounts.'
      );
    } finally {
      this.isSavingFinancials.set(false);
    }
  }

  constructor() {
    // React to invoice changes — subscribe to live streams of the loads and the invoice document
    effect(() => {
      const inv = this.initialInvoice();
      const companyId = this.authService.companyId();

      this.isEditingFinancials.set(false);
      this.invoiceSub?.unsubscribe();
      if (inv?.id) {
        this.invoiceSub = this.invoiceService.getInvoiceById(inv.id).subscribe({
          next: (live) => {
            if (live) {
              this.liveInvoice.set(live);

              // Auto-heal schedule-reconciled invoices where `receivedAmount` was set to the pre-wire
              // Advance Amount instead of subtracting the schedule-level `transactionFee` (e.g. -$5.99 Balance Due)
              const txFee = Number(
                live.factoringFees?.transactionFee ??
                  live.scheduleExpensesDeducted ??
                  0
              );
              const expectedNet = Number(
                ((live.totalAmount || 0) - (live.feeAmount || 0)).toFixed(2)
              );
              const currentReceived = Number(live.receivedAmount || 0);
              if (
                live.id &&
                live.status === 'paid' &&
                txFee > 0 &&
                expectedNet > 0 &&
                Math.abs(currentReceived - txFee - expectedNet) <= 0.05
              ) {
                this.invoiceService
                  .updateInvoiceFinancialSummary(live.id, {
                    receivedAmount: expectedNet,
                    feeAmount: Number(live.feeAmount || 0),
                  })
                  .catch(() => {});
              }

              if (live.loadSnapshots?.length) {
                this.loadSnapshots.update((current) =>
                  current.map((item) => {
                    const updatedSnap = live.loadSnapshots?.find((s) => s.loadId === item.loadId);
                    if (!updatedSnap) return item;
                    return {
                      ...item,
                      rawLoadRef: updatedSnap.rawLoadRef || item.rawLoadRef,
                      isSplitSchedulePayment:
                        updatedSnap.isSplitSchedulePayment ?? item.isSplitSchedulePayment,
                      paymentStatus: updatedSnap.paymentStatus || item.paymentStatus,
                      paidAmount:
                        typeof updatedSnap.paidAmount === 'number' && updatedSnap.paidAmount > 0
                          ? updatedSnap.paidAmount
                          : item.paidAmount,
                      pricing: { ...updatedSnap.pricing },
                    };
                  })
                );
              }
            }
          },
        });
      }

      if (!inv || !inv.loadIds?.length || !companyId) {
        this.loadSnapshots.set([]);
        return;
      }

      this.loadsSub?.unsubscribe();
      this.isLoadingLoads.set(true);

      this.loadsSub = this.loadService.liveLoadsByIdsWithViews$(inv.loadIds, companyId).subscribe({
        next: (loads) => {
          const activeInv = this.liveInvoice() || inv;
          const liveLoads: LiveInvoiceLoad[] = loads.map(l => {
            // Prefer immutable pricing from snapshot if available, else build from live view
            const snap = activeInv.loadSnapshots?.find(s => s.loadId === l.id);
            const rawView = (l.view || {}) as any;

            const settlementsMap: Record<string, any> =
              rawView.factoringSettlementsBySchedule || {};
            const matchingScheduleSettlement = Object.values(settlementsMap).find(
              (s: any) =>
                (activeInv.id && s?.invoiceId === activeInv.id) ||
                (activeInv.factoringScheduleIds?.length &&
                  s?.scheduleId &&
                  activeInv.factoringScheduleIds.includes(s.scheduleId))
            ) as any;

            const isMultiScheduleLoad =
              Boolean(snap?.isSplitSchedulePayment) ||
              (l.view?.factoringScheduleIds?.length || 0) > 1 ||
              Object.keys(settlementsMap).length > 1 ||
              (Array.isArray(rawView.invoiceIds) && rawView.invoiceIds.length > 1);

            const derivePaymentStatus = (): LiveInvoiceLoad['paymentStatus'] => {
              if (snap?.paymentStatus && snap.paymentStatus !== 'unpaid') {
                return snap.paymentStatus;
              }
              if (isMultiScheduleLoad && activeInv.status === 'paid') {
                return 'paid';
              }
              const nestedStatus = l.view?.pricing?.paymentStatus;
              if (nestedStatus && nestedStatus !== 'unpaid') return nestedStatus as any;
              const dotStatus = rawView['pricing.paymentStatus'];
              if (dotStatus && dotStatus !== 'unpaid') return dotStatus as any;
              if (l.view?.billingStatus === 'PAID') return 'paid';
              if (rawView.billingStatus === 'PARTIALLY_PAID' || rawView.billingStatus === 'SHORT_PAID') {
                return 'partially_paid';
              }
              if (activeInv.status === 'paid') return 'paid';
              return (nestedStatus || dotStatus || 'unpaid') as any;
            };

            const resolvedPaymentStatus = derivePaymentStatus();

            const derivePaidAmount = (grossTotal: number): number => {
              if (typeof snap?.paidAmount === 'number' && snap.paidAmount > 0) {
                return snap.paidAmount;
              }
              if (
                matchingScheduleSettlement &&
                typeof matchingScheduleSettlement.fundedAmount === 'number' &&
                matchingScheduleSettlement.fundedAmount > 0
              ) {
                return matchingScheduleSettlement.fundedAmount;
              }
              const candidates = [
                rawView.factoringSettlement?.scheduleId &&
                activeInv.factoringScheduleIds?.includes(rawView.factoringSettlement.scheduleId)
                  ? rawView.factoringSettlement.fundedAmount
                  : null,
                l.view?.pricing?.paidAmount,
                rawView['pricing.paidAmount'],
                l.view?.paidAmount,
                rawView.factoringSettlement?.fundedAmount,
              ];
              for (const val of candidates) {
                // Guard against using a cumulative or other-schedule paidAmount that exceeds this invoice line's gross total
                if (typeof val === 'number' && val > 0 && (grossTotal <= 0 || val <= grossTotal + 0.01)) {
                  return val;
                }
              }
              if (resolvedPaymentStatus === 'paid') {
                if (activeInv.totalAmount && activeInv.feeAmount) {
                  const proportion = grossTotal / activeInv.totalAmount;
                  return Math.round((grossTotal - activeInv.feeAmount * proportion) * 100) / 100;
                }
                return grossTotal;
              }
              return 0;
            };

            // Self-heal load view if nested pricing.paymentStatus was out of sync with billingStatus or dot-notation fields
            if (
              l.view &&
              !isMultiScheduleLoad &&
              resolvedPaymentStatus !== 'unpaid' &&
              l.view.pricing?.paymentStatus !== resolvedPaymentStatus
            ) {
              const healTotal = snap?.pricing?.total || 0;
              const healAmount = derivePaidAmount(healTotal);
              this.loadService
                .updateCompanyView(l.id, companyId, {
                  pricing: {
                    ...(l.view.pricing || ({ baseRate: healTotal, lumper: 0, fuelSurcharge: 0, otherCharges: 0, deductions: 0 } as any)),
                    status: resolvedPaymentStatus === 'paid' ? 'paid' : resolvedPaymentStatus === 'partially_paid' ? 'partially_paid' : 'invoiced',
                    paymentStatus: resolvedPaymentStatus as any,
                    paidAmount: healAmount,
                    invoiceId: activeInv.id,
                  },
                })
                .subscribe({ error: () => {} });
            }

            const pricing = l.view?.pricing;
            const lineItem = computeLoadLineItem(
              pricing,
              (l.view as any)?.bookedAtRate,
              l.view?.tonuDecision
            );

            // If the user explicitly saved updated pricing on this load via `EditPricingModalComponent`
            // (`pricing.totalAmount` is only set by `EditPricingModalComponent.save()`), check if the invoice's
            // snapshot is out of sync (e.g. from the previous undefined-field sync failure) and self-heal it.
            // Never auto-overwrite when the load is split across multiple schedules (`isMultiScheduleLoad`).
            const hasExplicitEditedPricing =
              !isMultiScheduleLoad &&
              pricing?.totalAmount != null &&
              Number(pricing.totalAmount) > 0;
            const isSnapOutOfSync =
              !!snap &&
              hasExplicitEditedPricing &&
              (Math.abs((snap.pricing?.total || 0) - lineItem.total) > 0.01 ||
                Math.abs((snap.pricing?.baseRate || 0) - lineItem.baseRate) > 0.01 ||
                Math.abs((snap.pricing?.lumper || 0) - lineItem.lumper) > 0.01 ||
                Math.abs((snap.pricing?.detention || 0) - lineItem.detention) > 0.01 ||
                Math.abs((snap.pricing?.layover || 0) - lineItem.layover) > 0.01);

            if (isSnapOutOfSync && activeInv.id) {
              this.invoiceService
                .syncInvoiceLoadPricing(activeInv.id, l.id, lineItem, {
                  otherChargesDescription: (pricing as any)?.otherChargesDescription || null,
                  deductionsDescription: (pricing as any)?.deductionsDescription || null,
                })
                .catch(() => {});
            }

            if (snap && !isSnapOutOfSync) {
              return {
                loadId: l.id,
                externalId: snap.externalId || l.externalId || undefined,
                rawLoadRef: snap.rawLoadRef,
                isSplitSchedulePayment: isMultiScheduleLoad,
                counterpartyName: snap.counterpartyName || resolveBookedWithCounterparty(l).name,
                pricing: { ...snap.pricing },
                paymentStatus: resolvedPaymentStatus,
                paidAmount: derivePaidAmount(snap.pricing?.total || 0),
              };
            }

            return {
              loadId: l.id,
              externalId: snap?.externalId || l.externalId || undefined,
              rawLoadRef: snap?.rawLoadRef,
              isSplitSchedulePayment: isMultiScheduleLoad,
              counterpartyName: snap?.counterpartyName || resolveBookedWithCounterparty(l).name,
              pricing: {
                baseRate: lineItem.baseRate,
                detention: lineItem.detention,
                detentionHours: lineItem.detentionHours,
                layover: lineItem.layover,
                lumper: lineItem.lumper,
                fuelSurcharge: lineItem.fuelSurcharge,
                otherCharges: lineItem.otherCharges,
                otherChargesDescription: (pricing as any)?.otherChargesDescription || null,
                deductions: lineItem.deductions,
                deductionsDescription: (pricing as any)?.deductionsDescription || null,
                tonu: lineItem.tonu,
                isTonu: lineItem.isTonu,
                total: lineItem.total,
              },
              paymentStatus: resolvedPaymentStatus,
              paidAmount: derivePaidAmount(lineItem.total || 0),
            };
          });
          
          this.loadSnapshots.set(liveLoads);
          this.isLoadingLoads.set(false);
        },
        error: () => {
          this.toastService.error('Failed to load invoice loads');
          this.isLoadingLoads.set(false);
        }
      });
    });
  }

  ngOnDestroy(): void {
    this.loadsSub?.unsubscribe();
    this.invoiceSub?.unsubscribe();
  }

  // --- Status / Badge Helpers ---


  getStatusVariant(status: string | undefined | null): ColorVariant {
    switch (status) {
      case 'paid': return 'success';
      case 'partially_paid': return 'warning';
      case 'processing': return 'info';
      case 'draft': return 'secondary';
      case 'sent': return 'primary';
      case 'overdue': return 'danger';
      case 'problematic': return 'danger';
      case 'voided':
      case 'VOIDED': return 'secondary';
      default: return 'default';
    }
  }

  getPaymentStatusVariant(status: string | undefined | null): ColorVariant {
    switch (status) {
      case 'paid': return 'success';
      case 'partially_paid': return 'warning';
      case 'unpaid': return 'secondary';
      case 'denied': return 'danger';
      case 'problematic': return 'danger';
      default: return 'default';
    }
  }

  getMethodVariant(method: string | undefined | null): ColorVariant {
    return method === 'factoring' ? 'info' : 'primary';
  }

  // --- Snapshot Helpers ---
  getLoadDisplayId(snap: LiveInvoiceLoad): string {
    return snap.externalId || snap.loadId?.substring(0, 8) || '—';
  }

  getLoadCounterparty(snap: LiveInvoiceLoad): string {
    return snap.counterpartyName || '—';
  }

  getLoadTotal(snap: LiveInvoiceLoad): number {
    return snap.pricing?.total || 0;
  }

  getLoadNetAmount(snap: LiveInvoiceLoad): number {
    const inv = this.invoice();
    const loadTotal = this.getLoadTotal(snap);
    if (!inv || !inv.totalAmount || inv.totalAmount === 0 || !inv.feeAmount) {
      return loadTotal;
    }
    const proportion = loadTotal / inv.totalAmount;
    const loadFee = inv.feeAmount * proportion;
    return loadTotal - loadFee;
  }

  getLoadPaymentStatus(snap: LiveInvoiceLoad): string {
    const pending = this.pendingChanges().get(snap.loadId);
    if (pending) return pending.status;
    return snap.paymentStatus || 'unpaid';
  }

  getLoadPaidAmount(snap: LiveInvoiceLoad): number {
    const pending = this.pendingChanges().get(snap.loadId);
    if (pending) return pending.amount;
    
    // If not paid yet, default to the proportional net amount (not gross total)
    if (!snap.paidAmount && snap.paymentStatus === 'unpaid') {
      return this.getLoadNetAmount(snap);
    }
    return snap.paidAmount || 0;
  }

  showAmountInput(snap: LiveInvoiceLoad): boolean {
    const status = this.getLoadPaymentStatus(snap);
    return status === 'paid' || status === 'partially_paid';
  }



  // --- Manual Save Management ---
  setPendingStatus(snap: LiveInvoiceLoad, newStatus: string): void {
    const changes = new Map(this.pendingChanges());
    const currentAmount = snap.paidAmount || 0;
    const existing = changes.get(snap.loadId) || { status: snap.paymentStatus || 'unpaid', amount: currentAmount };
    
    // Default amount based on the new status
    let amountToUse = existing.amount;
    if (newStatus === 'paid') {
      amountToUse = this.getLoadNetAmount(snap);
    } else if (newStatus === 'unpaid' || newStatus === 'denied' || newStatus === 'problematic') {
      amountToUse = 0;
    }

    changes.set(snap.loadId, { ...existing, status: newStatus, amount: amountToUse });
    this.pendingChanges.set(changes);
  }

  setPendingAmount(snap: LiveInvoiceLoad, newAmount: number): void {
    const changes = new Map(this.pendingChanges());
    const existing = changes.get(snap.loadId) || { status: snap.paymentStatus || 'unpaid', amount: snap.paidAmount || 0 };
    changes.set(snap.loadId, { ...existing, amount: newAmount });
    this.pendingChanges.set(changes);
  }

  hasPendingChange(loadId: string): boolean {
    return this.pendingChanges().has(loadId);
  }

  isSavingAll = signal(false);

  discardPendingChanges(): void {
    this.pendingChanges.set(new Map());
  }

  async saveAllPendingChanges(): Promise<void> {
    const changesMap = this.pendingChanges();
    if (changesMap.size === 0) return;
    
    const invoice = this.invoice();
    if (!invoice?.id) return;
    
    this.isSavingAll.set(true);

    try {
      // Build the changes array
      const changesList = Array.from(changesMap.entries()).map(([loadId, change]) => ({
        loadId,
        paymentStatus: change.status,
        paidAmount: change.amount
      }));

      // Build the updated aggregate array of all loads
      const allLoadPayments = this.loadSnapshots().map(s => {
        const pending = changesMap.get(s.loadId);
        return {
          loadId: s.loadId,
          paymentStatus: pending ? pending.status : (s.paymentStatus || 'unpaid'),
          paidAmount: pending ? pending.amount : (s.paidAmount || 0),
        };
      });

      await this.invoiceService.updateMultipleLoadPayments(
        invoice.id,
        changesList,
        allLoadPayments
      );

      // Clear pending changes on success
      this.pendingChanges.set(new Map());
      this.toastService.success('Payments updated successfully');
    } catch (err) {
      console.error('Failed to update load payments', err);
      this.toastService.error('Failed to update load payments');
    } finally {
      this.isSavingAll.set(false);
    }
  }

  // --- Permission ---
  canEditLoadPayments(): boolean {
    const status = this.invoice()?.status;
    return !!status && status !== 'draft' && status !== 'processing' && status !== 'voided' && status !== 'VOIDED';
  }

  canRecordPayment(): boolean {
    const status = this.invoice()?.status;
    if (!status || status === 'draft' || status === 'processing' || status === 'paid' || status === 'voided' || status === 'VOIDED') {
      return false;
    }
    return this.balanceDue() > 0.01;
  }

  isArchiving = signal(false);

  async toggleArchiveInvoice(): Promise<void> {
    const inv = this.invoice();
    if (!inv?.id) return;
    const nextArchived = !inv.isArchived;
    this.isArchiving.set(true);
    try {
      await this.invoiceService.archiveInvoices([inv.id], nextArchived);
      this.toastService.show(
        'success',
        nextArchived
          ? `Invoice #${inv.invoiceNumber} archived and hidden from active views.`
          : `Invoice #${inv.invoiceNumber} restored from archive.`
      );
      if (nextArchived) {
        this.asideService.close();
      }
    } catch (err: any) {
      this.toastService.show('error', `Failed to update archive status: ${err?.message || 'Unknown error'}`);
    } finally {
      this.isArchiving.set(false);
    }
  }

  // --- Date Formatting ---
  formatDate(timestamp: any): string {
    if (!timestamp) return '—';
    if (timestamp?.toDate) {
      return timestamp.toDate().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    }
    if (timestamp instanceof Date) {
      return timestamp.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    }
    return '—';
  }

  formatStatusLabel(status: string | undefined | null): string {
    if (!status) return '—';
    return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  openVoidModal(): void {
    const inv = this.invoice();
    if (!inv) return;
    this.modalService.open(VoidInvoiceModalComponent, {
      title: 'Void Invoice',
      subtitle: `Invoice #${inv.invoiceNumber}`,
      icon: 'Ban',
      size: 'md',
      data: {
        invoices: [inv],
        onSuccess: () => {
          this.asideService.close();
        },
      },
    });
  }

  openRecordPaymentModal(): void {
    const inv = this.invoice();
    if (!inv) return;
    if (!this.canRecordPayment()) {
      this.toastService.show('error', 'Payment can only be recorded after the invoice has been submitted successfully.');
      return;
    }
    this.modalService.open(RecordPaymentModalComponent, {
      title: 'Record Payment',
      subtitle: `Invoice #${inv.invoiceNumber}`,
      icon: 'CheckCircle',
      size: 'md',
      data: {
        invoice: inv,
        onSuccess: () => {
          // Subscriptions automatically update the invoice and loads
        },
      },
    });
  }

  // --- Document Management ---

  openUploadScheduleModal(): void {
    const inv = this.invoice();
    if (!inv) return;
    this.modalService.open(UploadScheduleModalComponent, {
      title: 'Upload Payout Schedule / Remittance',
      subtitle: `AI-extract line items & fees (linked to Invoice #${inv.invoiceNumber})`,
      icon: 'FileSpreadsheet',
      size: 'md',
      data: {
        hintInvoiceId: inv.id,
        hintInvoiceNumber: inv.invoiceNumber,
        defaultScheduleType: inv.invoicingMethod === 'direct' ? 'direct_remittance' : 'factoring',
      },
    });
  }

  openUploadDocumentModal(defaultType: InvoiceDocumentType = 'factoring_schedule'): void {
    const inv = this.invoice();
    if (!inv) return;
    if (defaultType === 'factoring_schedule') {
      this.openUploadScheduleModal();
      return;
    }
    this.modalService.open(UploadInvoiceDocumentModalComponent, {
      title: 'Upload Invoice Document',
      subtitle: `Invoice #${inv.invoiceNumber}`,
      icon: 'Upload',
      size: 'md',
      data: {
        invoiceId: inv.id,
        invoiceNumber: inv.invoiceNumber,
        defaultType,
      },
    });
  }

  openDocument(docItem: InvoiceDocument): void {
    if (!docItem.fileUrl) return;
    const isPdf =
      docItem.type === 'factoring_schedule' ||
      docItem.type === 'invoice_pdf' ||
      docItem.fileName?.toLowerCase().endsWith('.pdf') ||
      docItem.fileUrl.toLowerCase().includes('.pdf');
    this.modalService.open(DocumentViewerModalComponent, {
      title: docItem.title || docItem.fileName || this.getDocumentTypeLabel(docItem.type),
      subtitle: docItem.fileName,
      icon: docItem.type === 'factoring_schedule' ? 'FileSpreadsheet' : 'FileText',
      size: 'xl',
      data: {
        url: docItem.fileUrl,
        documentType: isPdf ? 'pdf' : 'image',
      },
    });
  }

  openDocumentExternal(docItem: InvoiceDocument): void {
    if (docItem.fileUrl) {
      window.open(docItem.fileUrl, '_blank', 'noopener,noreferrer');
    }
  }

  async deleteDocument(docItem: InvoiceDocument): Promise<void> {
    const inv = this.invoice();
    if (!inv) return;

    const confirmed = await this.modalService.confirm({
      title: 'Delete Document',
      message: `Are you sure you want to permanently delete "${docItem.title || docItem.fileName}"?`,
      confirmText: 'Delete',
      variant: 'destructive',
    });

    if (confirmed) {
      try {
        await this.invoiceService.deleteInvoiceDocument(inv.id, docItem.id);
        this.toastService.show('success', 'Document deleted successfully.');
      } catch (err: any) {
        this.toastService.show('error', `Failed to delete document: ${err?.message || 'Unknown error'}`);
      }
    }
  }

  getDocumentTypeLabel(type: InvoiceDocumentType): string {
    switch (type) {
      case 'factoring_schedule':
        return 'Factoring Schedule';
      case 'payment_proof':
        return 'Payment Proof';
      case 'notice_of_assignment':
        return 'Notice of Assignment';
      case 'invoice_pdf':
        return 'Invoice PDF';
      default:
        return 'Supporting Document';
    }
  }

  openResendModal(): void {
    const inv = this.invoice();
    if (!inv) return;
    this.modalService.open(ResendInvoiceModalComponent, {
      title: `Resend Invoice #${inv.invoiceNumber}`,
      subtitle: 'Update recipient email addresses and resend the complete invoice package',
      icon: 'Send',
      size: 'md',
      data: {
        invoice: inv,
        onSuccess: () => {
          // Live invoice subscription automatically updates the view
        },
      },
    });
  }

  async openEditLoadPricing(snap: InvoiceLoadSnapshot): Promise<void> {
    const inv = this.invoice();
    if (!inv) return;

    try {
      const companyId = this.authService.activeCompanyId();
      if (!companyId) return;

      const fullLoad = await firstValueFrom(this.loadService.loadById$(snap.loadId));
      if (!fullLoad) {
        this.toastService.show('error', 'Load not found');
        return;
      }
      const view = await this.loadService.getCompanyViewOnce(snap.loadId, companyId);

      const snapPricing = snap.pricing;
      const viewPricing = view?.pricing;
      const isCancelledOrTonu =
        fullLoad.status === 'CANCELLED' ||
        Boolean(snapPricing?.isTonu || (snapPricing?.tonu && snapPricing.tonu > 0)) ||
        view?.tonuDecision?.status === 'eligible' ||
        view?.tonuDecision?.status === 'pending';

      const resolvedBaseRate =
        viewPricing?.baseRate != null && viewPricing.baseRate > 0
          ? viewPricing.baseRate
          : snapPricing?.baseRate != null && snapPricing.baseRate > 0
            ? snapPricing.baseRate
            : Number((view as any)?.bookedAtRate ?? viewPricing?.baseRate ?? snapPricing?.baseRate ?? 0);

      const mergedPricing = {
        ...(snapPricing || {}),
        ...(viewPricing || {}),
        baseRate: resolvedBaseRate,
        detention: viewPricing?.detention ?? snapPricing?.detention ?? 0,
        detentionHours: viewPricing?.detentionHours ?? snapPricing?.detentionHours ?? 0,
        layover: viewPricing?.layover ?? snapPricing?.layover ?? 0,
        lumper: viewPricing?.lumper ?? snapPricing?.lumper ?? 0,
        fuelSurcharge: viewPricing?.fuelSurcharge ?? snapPricing?.fuelSurcharge ?? 0,
        otherCharges: viewPricing?.otherCharges ?? snapPricing?.otherCharges ?? 0,
        otherChargesDescription:
          (viewPricing as any)?.otherChargesDescription ?? snapPricing?.otherChargesDescription ?? '',
        deductions: viewPricing?.deductions ?? snapPricing?.deductions ?? 0,
        deductionsDescription:
          (viewPricing as any)?.deductionsDescription ?? snapPricing?.deductionsDescription ?? '',
        tonu: viewPricing?.tonu ?? snapPricing?.tonu ?? view?.tonuDecision?.amount ?? 0,
      };

      const mergedTonuDecision = isCancelledOrTonu
        ? view?.tonuDecision
          ? {
              ...view.tonuDecision,
              amount: view.tonuDecision.amount ?? snapPricing?.tonu ?? viewPricing?.tonu ?? 150,
            }
          : {
              status: 'eligible' as const,
              amount: snapPricing?.tonu ?? viewPricing?.tonu ?? 150,
            }
        : view?.tonuDecision;

      const loadWithView: LoadWithView = {
        ...fullLoad,
        id: snap.loadId,
        view: {
          ...(view || ({ loadId: snap.loadId, companyId } as any)),
          pricing: mergedPricing,
          ...(mergedTonuDecision ? { tonuDecision: mergedTonuDecision } : {}),
        },
      };

      const syncLoadToInvoice = async (viewPatch?: any) => {
        try {
          const updatedView = await this.loadService.getCompanyViewOnce(snap.loadId, companyId);
          const p = viewPatch?.pricing ?? updatedView?.pricing ?? mergedPricing;
          const tonuDec =
            viewPatch?.tonuDecision ?? updatedView?.tonuDecision ?? loadWithView.view?.tonuDecision;
          const bookedRate = (updatedView as any)?.bookedAtRate ?? (view as any)?.bookedAtRate;
          const lineItem = computeLoadLineItem(p, bookedRate, tonuDec);

          await this.invoiceService.syncInvoiceLoadPricing(inv.id, snap.loadId, lineItem, {
            otherChargesDescription: (p as any)?.otherChargesDescription || null,
            deductionsDescription: (p as any)?.deductionsDescription || null,
          });

          this.loadSnapshots.update((snaps) =>
            snaps.map((s) =>
              s.loadId === snap.loadId
                ? {
                    ...s,
                    pricing: {
                      ...s.pricing,
                      baseRate: lineItem.baseRate,
                      detention: lineItem.detention,
                      detentionHours: lineItem.detentionHours,
                      layover: lineItem.layover,
                      lumper: lineItem.lumper,
                      fuelSurcharge: lineItem.fuelSurcharge,
                      otherCharges: lineItem.otherCharges,
                      otherChargesDescription:
                        ((p as any)?.otherChargesDescription ?? s.pricing?.otherChargesDescription) || null,
                      deductions: lineItem.deductions,
                      deductionsDescription:
                        ((p as any)?.deductionsDescription ?? s.pricing?.deductionsDescription) || null,
                      tonu: lineItem.tonu,
                      isTonu: lineItem.isTonu,
                      total: lineItem.total,
                    },
                  }
                : s
            )
          );

          this.toastService.show('success', 'Invoice pricing updated to reflect load changes.');
        } catch (syncErr: any) {
          console.error('Failed to sync invoice pricing:', syncErr);
          this.toastService.show(
            'error',
            syncErr?.message || 'Failed to sync updated load pricing to invoice.'
          );
        }
      };

      if (isCancelledOrTonu) {
        this.modalService.open(TonuDecisionModalComponent, {
          title: `TONU Decision — Load #${snap.externalId || snap.loadId.substring(0, 8)}`,
          subtitle: `Invoice #${inv.invoiceNumber}`,
          icon: 'CircleSlash',
          size: 'md',
          data: {
            load: loadWithView,
            onSuccess: syncLoadToInvoice,
          },
        });
      } else {
        this.modalService.open(EditPricingModalComponent, {
          title: `Edit Pricing — Load #${snap.externalId || snap.loadId.substring(0, 8)}`,
          subtitle: `Invoice #${inv.invoiceNumber}`,
          icon: 'DollarSign',
          size: 'lg',
          data: {
            load: loadWithView,
            onSuccess: syncLoadToInvoice,
          },
        });
      }
    } catch (err: any) {
      console.error('Failed to open edit pricing:', err);
      this.toastService.show('error', 'Failed to load pricing details');
    }
  }

  openRemoveLoad(snap: InvoiceLoadSnapshot): void {
    const inv = this.invoice();
    if (!inv) return;

    this.modalService.open(RemoveLoadModalComponent, {
      title: 'Remove Load from Invoice',
      subtitle: `Invoice #${inv.invoiceNumber}`,
      icon: 'Trash2',
      size: 'md',
      data: {
        invoice: inv,
        loadSnapshot: snap,
        onSuccess: (result: { remainingLoadsCount: number; newTotal: number }) => {
          if (result.remainingLoadsCount === 0) {
            this.asideService.close();
          } else {
            this.loadSnapshots.update((snaps) => snaps.filter((s) => s.loadId !== snap.loadId));
          }
        },
      },
    });
  }
}
